<script setup>
// Astra DS · VideoFacade — la portada de un vídeo, que se convierte en el vídeo.
//
// Contrato: `TV_WEB_INSTRUCCIONES.md` §4, «Portada + reproducción in situ», que
// el servidor marca como **REQUERIDO**.
//
// 🔴 **El iframe NO existe hasta que alguien pulsa.** Cada iframe de YouTube
// pesa cerca de un mega y planta cookies; con treinta y nueve vídeos en una
// página, cargarlos al pintar es la diferencia entre una página y un disparate.
// Lo que se pinta es la portada —`thumbnail_url`, que elige el equipo de la
// radio en su dashboard— con el play encima.
//
// Al pulsar, la portada se desvanece en 300 ms y el vídeo se reproduce **en el
// mismo sitio**: no hay modal, no hay navegación. Y una vez reproduciendo, los
// clics son del iframe —controles, pantalla completa—, así que la portada deja
// de recibirlos.
//
// ⚠️ En la portada del sitio esto es además una **prohibición**: la tarjeta de
// vídeo de la home no puede ser un enlace a la sección TV. Se amplía desde el
// propio reproductor, y por eso el iframe lleva `allowfullscreen`.
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onDeactivated,
} from "vue";
import DuotoneArt from "./DuotoneArt.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  // La URL pública del vídeo. De aquí sale el id de YouTube.
  url: { type: String, default: "" },

  // La portada. Se usa la que da el contrato —el dashboard la elige o la
  // personaliza— y NO se construye a mano.
  poster: { type: String, default: "" },
  // A qué volver si esa portada no resuelve. `DuotoneArt` la rescata tanto si
  // falla como si carga vacía, que es lo que hace YouTube con las miniaturas
  // que no existe: 404 con una imagen gris de 120×90 dentro.
  posterFallback: { type: String, default: "" },

  title: { type: String, default: "" },
  // Etiqueta de esquina sobre la portada: el programa del que sale la sesión.
  tag: { type: String, default: "" },
  tagAnchor: { type: String, default: "top" },

  playAnchor: { type: String, default: "bottom-left" },
  // El lado del play. Ver la nota de `DuotoneArt`: 54 en las tarjetas, 65 en el
  // visor de Butaca, que es la medida del play de la emisión.
  playLado: { type: Number, default: 54 },

  /**
   * El recorte del fragmento, en segundos.
   *
   * 🔴 Lo estrena Butaca · secuencias el 21.09.26. **Una secuencia no es un
   * vídeo entero, es un trozo**, y el contrato lo dice con todas las letras:
   * «respetad siempre los dos límites; sin ellos el visitante ve el vídeo entero
   * y se pierde el sentido de la sección».
   *
   * ⚠️ No se usa el `embed_url` que da el servidor aunque venga hecho: ese no
   * lleva `enablejsapi`, y sin él el iframe es una caja cerrada —no se sabe si
   * suena ni se le puede pedir que pare—, así que la radio y el vídeo sonarían a
   * la vez. Se montan aquí los mismos límites sobre el embed de la casa.
   */
  desde: { type: Number, default: null },
  hasta: { type: Number, default: null },

  // Si la portada recupera el color al pasar por encima.
  //
  // Apagado por defecto, que es como nació y como sigue en la sección TV: allí
  // la fachada ES el reproductor y la imagen no lleva a ninguna parte, así que
  // el color no tendría nada que anunciar.
  //
  // La puerta de la portada lo enciende: allí la fachada vive dentro de una
  // tarjeta que sí es una puerta, con dos vecinas que recuperan color al pasar.
  // Que la del medio se quedara en gris era la diferencia que se notaba.
  hoverColor: { type: Boolean, default: false },
});

// `play` y `pause` son lo que de verdad hace el VÍDEO, no lo que pulsa nadie:
// salen del reproductor de YouTube, así que también avisan cuando el usuario
// usa los controles de dentro del iframe.
const emit = defineEmits(["play", "pause"]);

// Dos estados distintos, y hace falta separarlos: `montado` es «el iframe
// existe» —una vez pulsas, ya no se desmonta—, y `sonando` es «está en
// marcha». Antes solo había el primero, y por eso pausar dentro del iframe no
// se enteraba nadie.
const reproduciendo = ref(false);
const sonando = ref(false);
const marco = ref(null);

const idYouTube = computed(() => {
  const m = String(props.url || "").match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  return m ? m[1] : "";
});

// `rel=0` para que al acabar no ofrezca vídeos de otra gente.
//
// 🔴 `enablejsapi=1` no es opcional: sin él, el iframe es una caja cerrada. No
// se puede saber si el vídeo está sonando —ni cuando lo pausan desde sus
// propios controles— ni se le puede pedir que pare. Las dos cosas hacen falta
// para que la radio y el vídeo no suenen a la vez, que es regla de producto.
//
// `origin` lo pide YouTube para aceptar los mensajes: sin él, ignora los
// comandos que le mandemos.
const embed = computed(() => {
  if (!idYouTube.value) return "";
  const origen =
    typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  const recorte =
    (props.desde ? `&start=${Math.round(props.desde)}` : "") +
    (props.hasta ? `&end=${Math.round(props.hasta)}` : "");
  return `https://www.youtube.com/embed/${idYouTube.value}?autoplay=1&rel=0&enablejsapi=1&origin=${origen}${recorte}`;
});

// El protocolo del IFrame Player API por debajo: son `postMessage` con esta
// forma. Se usa directamente en vez de cargar `iframe_api` de YouTube —otro
// script externo, otro mega— porque solo hacen falta dos cosas de toda la API.
function mandaAlVideo(mensaje) {
  const ventana = marco.value?.contentWindow;
  if (!ventana) return;
  ventana.postMessage(JSON.stringify(mensaje), "https://www.youtube.com");
}

// YouTube no manda nada hasta que se le pide escuchar. Va en el `load` del
// iframe: antes, el `contentWindow` todavía no recibe.
function alCargarElMarco() {
  mandaAlVideo({ event: "listening", id: idYouTube.value });
  // El iframe entra con `autoplay=1`, así que si ha arrancado ya está sonando.
  // No se espera al primer `onStateChange`: puede tardar, y mientras tanto la
  // radio se creería sola.
  if (reproduciendo.value && !sonando.value) {
    sonando.value = true;
    emit("play");
  }
}

// Estados del reproductor de YouTube: 1 reproduciendo · 2 en pausa · 0 acabado.
const REPRODUCIENDO = 1;

function alMensaje(evento) {
  if (!/^https:\/\/(www\.)?youtube(-nocookie)?\.com$/.test(evento.origin)) return;
  // Puede haber varios VideoFacade en la misma página —la sección TV tiene una
  // rejilla entera—, así que cada uno atiende SOLO a su propio iframe.
  if (evento.source !== marco.value?.contentWindow) return;

  let datos;
  try {
    datos = typeof evento.data === "string" ? JSON.parse(evento.data) : evento.data;
  } catch {
    return;
  }
  if (datos?.event !== "onStateChange" && datos?.event !== "infoDelivery") return;

  const estado = datos.info?.playerState ?? datos.info;
  if (typeof estado !== "number") return;

  const ahora = estado === REPRODUCIENDO;
  if (ahora === sonando.value) return;
  sonando.value = ahora;
  emit(ahora ? "play" : "pause");
}

onMounted(() => window.addEventListener("message", alMensaje));
onBeforeUnmount(() => {
  window.removeEventListener("message", alMensaje);
  soltar();
});

// 🔴 Salir de la página tiene que apagar el vídeo, y no basta con confiar en
// que se desmonte.
//
// La portada y Radio van con `keep-alive`: la vista no se destruye, se guarda y
// vuelve. Pero Vue saca su DOM del documento, y **un iframe reinsertado se
// recarga**, con su `autoplay=1` intacto. El resultado que reportó Pablo: dejas
// el vídeo pausado, navegas, vuelves, y arranca solo.
//
// Se vuelve a la portada al salir. El iframe se destruye igualmente al sacarlo
// del DOM —el vídeo ya se corta hoy al navegar—, así que no se pierde nada que
// no estuviera perdido: lo que se gana es que al volver haya una portada con su
// play, y no un vídeo sonando sin que nadie lo haya pedido.
function soltar() {
  // Si se iba sonando, hay que decirlo: el store cree que la fuente es el
  // vídeo, y sin soltarla se quedaría marcada para siempre.
  if (sonando.value) emit("pause");
  detener();
}

onDeactivated(soltar);

function reproducir() {
  if (!embed.value) return;

  // Ya montado: el iframe existe y lo que se quiere es reanudarlo. Pasa cuando
  // el vídeo se pausó desde fuera —al dar play a la radio— y se vuelve a él.
  if (reproduciendo.value) {
    mandaAlVideo({ event: "command", func: "playVideo", args: [] });
    return;
  }

  reproduciendo.value = true;
  // El `play` no se emite aquí. Lo emite el reproductor cuando arranca de
  // verdad, en `alCargarElMarco` o en el primer `onStateChange`: si el vídeo
  // no llegara a sonar, la radio se habría callado para nada.
}

// Pausa el vídeo desde fuera, dejando el iframe montado. Es lo que se le pide
// cuando alguien da al play de la radio: no se destruye el reproductor, porque
// eso perdería el minuto por el que iba.
function pausar() {
  if (!reproduciendo.value) return;
  mandaAlVideo({ event: "command", func: "pauseVideo", args: [] });
}

// Vuelve a la portada. Hace falta al CAMBIAR de vídeo en el mismo reproductor:
// sin esto, el iframe anterior seguiría montado con el vídeo viejo.
function detener() {
  reproduciendo.value = false;
  sonando.value = false;
}

// La sección TV arranca el reproductor principal desde fuera: pulsas una
// miniatura y el vídeo se carga arriba, ya en marcha.
defineExpose({ reproducir, pausar, detener, sonando });
</script>

<template>
  <div
    class="video-facade"
    :class="{ 'is-playing': reproduciendo }"
    :data-sonando="sonando ? 'si' : 'no'"
    @click="reproducir"
  >
    <iframe
      v-if="reproduciendo"
      ref="marco"
      class="video-facade__frame"
      :src="embed"
      @load="alCargarElMarco"
      :title="title"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowfullscreen
      frameborder="0"
    />

    <!-- La portada va ENCIMA del iframe y se desvanece: así el vídeo ya está
         cargando por debajo mientras la imagen se va. -->
    <DuotoneArt
      class="video-facade__cover"
      :src="poster"
      :fallback="posterFallback"
      :alt="title"
      :tag="tag"
      :tag-anchor="tagAnchor"
      :show-play="!reproduciendo"
      :play-anchor="playAnchor"
      :play-lado="playLado"
      :play-label="t('actualidad.video.reproducir')"
      ratio="auto"
      position="center"
      :hover-color="hoverColor"
      @play="reproducir"
    />
  </div>
</template>

<style scoped lang="scss">
// 🔴 La proporción la pone el REPRODUCTOR, no su hueco — es lo que dice el
// contrato (`.tv-player { aspect-ratio: 16/9 }`) y es lo correcto: un vídeo es
// 16:9 y recortarlo no tiene sentido.
//
// Con `height: 100%` en vez de esto, dentro de un hueco que deriva su altura de
// una proporción, el reproductor medía **0×0**: pedía el 100% de una altura que
// aún no existía, y como todo su contenido va en posición absoluta, tampoco
// aportaba nada al cálculo. La puerta de TV de la portada salía en negro.
.video-facade {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  // Negro real, no el lienzo del tema: es el fondo de un vídeo, y detrás
  // de un fotograma no va lino.
  background: #000;
  cursor: pointer;
  overflow: hidden;
}

.video-facade.is-playing {
  cursor: default;
}

.video-facade__frame {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  border: 0;
}

.video-facade__cover {
  position: absolute;
  inset: 0;
  z-index: 2;
  transition: opacity 0.3s ease;
}

// Una vez reproduciendo, la portada se va y deja de estorbar: los clics son
// del iframe.
.video-facade.is-playing .video-facade__cover {
  opacity: 0;
  pointer-events: none;
}

// Sin animación para quien pidió no verlas: la portada desaparece y ya.
@media (prefers-reduced-motion: reduce) {
  .video-facade__cover {
    transition: none;
  }
}
</style>
